import { MonoidSum } from '@effect/typeclass/data/Number'
import { Discount } from '@medusajs/medusa'
import { combineArray } from '@miles/prelude/array'
import { Array, Equivalence, Option, pipe } from 'effect'
import { PriceChange } from 'domain/models/Store/AdminProduct'
import { CommonCreateDiscount } from 'domain/models/Store/Discount'
import { Currency } from 'domain/models/Store/Product'
import { RaceId } from 'domain/models/ValueObject/EntityIds'
import { PeyceDiscount } from 'store/types'

export interface EstimatedDiscountFromPriceChange
  extends Omit<CommonCreateDiscount, 'editionId' | 'code' | 'ruleType'> {
  discountId: string | undefined
}

export interface EstimatedDiscountFromPriceChangeWithDiscountId
  extends Omit<CommonCreateDiscount, 'editionId' | 'code' | 'ruleType'> {
  discountId: string
}
interface PriceChangeWithDiscountId extends PriceChange {
  discountId: string
}

type PriceChangeOrPriceChangeWithDiscountId =
  | PriceChange
  | PriceChangeWithDiscountId

const EstimatedDiscountEqv = {
  startsAt: Equivalence.mapInput(
    Equivalence.Date,
    ({ startsAt }: EstimatedDiscountFromPriceChange) => startsAt
  ),
  endsAt: Equivalence.mapInput(
    Option.getEquivalence(Equivalence.Date),
    ({ endsAt }: EstimatedDiscountFromPriceChange) =>
      Option.fromNullable(endsAt)
  ),
  value: Equivalence.mapInput(
    Equivalence.number,
    ({ value }: EstimatedDiscountFromPriceChange) => value
  ),
  usageLimit: Equivalence.mapInput(
    Option.getEquivalence(Equivalence.number),
    ({ usageLimit }: EstimatedDiscountFromPriceChange) =>
      Option.fromNullable(usageLimit)
  )
}

const combinedEquivalenceEstimatedDiscount = Equivalence.combineAll([
  EstimatedDiscountEqv.startsAt,
  EstimatedDiscountEqv.endsAt,
  EstimatedDiscountEqv.value,
  EstimatedDiscountEqv.usageLimit
])

export const AutoDiscountUtils = {
  generateAutoDiscountCode: (raceId: RaceId): string =>
    `AUTO_DISCOUNT_${raceId}_${crypto.randomUUID()}`,
  estimatedDiscountFromPriceChange: (
    startsAt: Date,
    priceChangeList: PriceChangeOrPriceChangeWithDiscountId[]
  ): EstimatedDiscountFromPriceChange[] => {
    // need to duplicate the array to avoid pop mutating the original array
    const priceListDuplicate = [...priceChangeList]
    if (priceChangeList.length <= 1) {
      return []
    }

    const defaultPriceList = priceListDuplicate.pop()
    if (!defaultPriceList || defaultPriceList.price === null) {
      return []
    }
    return priceListDuplicate.map((priceChange) => ({
      value: defaultPriceList.price - priceChange.price,
      startsAt,
      endsAt: priceChange.date ?? undefined,
      usageLimit: priceChange.maxRegistrations ?? undefined,
      discountId:
        'discountId' in priceChange && priceChange.discountId !== ''
          ? priceChange.discountId
          : undefined
    }))
  },

  compareDiscountsCreate: (
    existing: EstimatedDiscountFromPriceChangeWithDiscountId[],
    estimated: EstimatedDiscountFromPriceChange[]
  ): EstimatedDiscountFromPriceChange[] =>
    estimated.filter(
      (candidate) =>
        !existing.some((item) => item.discountId === candidate.discountId)
    ),
  compareDiscountsDelete: (
    existing: EstimatedDiscountFromPriceChangeWithDiscountId[],
    estimated: EstimatedDiscountFromPriceChange[]
  ): EstimatedDiscountFromPriceChangeWithDiscountId[] =>
    existing.filter(
      (candidate) =>
        !estimated.some((item) => item.discountId === candidate.discountId)
    ),
  compareDiscountsUpdate: (
    existing: EstimatedDiscountFromPriceChangeWithDiscountId[],
    estimated: EstimatedDiscountFromPriceChange[]
  ): EstimatedDiscountFromPriceChangeWithDiscountId[] =>
    pipe(
      estimated,
      Array.filterMap(({ discountId, ...rest }) =>
        discountId ? Option.some({ ...rest, discountId }) : Option.none()
      ),
      Array.filter((candidate) => {
        const item = existing.find(
          (item) => item.discountId === candidate.discountId
        )
        return !!item && !combinedEquivalenceEstimatedDiscount(item, candidate)
      })
    ),
  compareDiscountsChanges: (
    existingAutoDiscount: Discount[],
    estimatedAutoDiscount: EstimatedDiscountFromPriceChange[]
  ): {
    create: EstimatedDiscountFromPriceChange[]
    update: EstimatedDiscountFromPriceChangeWithDiscountId[]
    remove: EstimatedDiscountFromPriceChangeWithDiscountId[]
  } => {
    if (existingAutoDiscount.length === 0) {
      return {
        create: estimatedAutoDiscount,
        update: [],
        remove: []
      }
    }

    const existingAutoDiscountToEstimatedPriceChange = existingAutoDiscount.map(
      AutoDiscountUtils.convertPeyceDiscountToEstimatedDiscountWithId
    )

    const result = {
      create: AutoDiscountUtils.compareDiscountsCreate(
        existingAutoDiscountToEstimatedPriceChange,
        estimatedAutoDiscount
      ),
      update: AutoDiscountUtils.compareDiscountsUpdate(
        existingAutoDiscountToEstimatedPriceChange,
        estimatedAutoDiscount
      ),
      remove: AutoDiscountUtils.compareDiscountsDelete(
        existingAutoDiscountToEstimatedPriceChange,
        estimatedAutoDiscount
      )
    }
    return result
  },
  convertDiscountToPriceChangeWithId:
    (priceInCents: number) =>
    (discounts: Discount[]): (PriceChange & { discountId: string })[] => {
      return pipe(
        discounts,
        Array.map(
          ({ rule: { value }, usage_limit, usage_count, ends_at, id }) => ({
            price: (priceInCents - value) / 100,
            maxRegistrations: usage_limit,
            usageCount: usage_count,
            date: ends_at ? new Date(ends_at) : null,
            discountId: id
          })
        ),
        Array.append({
          price: priceInCents / 100,
          maxRegistrations: null,
          date: null,
          usageCount: null,
          discountId: ''
        })
      )
    },
  convertPeyceDiscountToEstimatedDiscountWithId: ({
    id: discountId,
    rule: { value },
    starts_at,
    ends_at,
    usage_limit
  }: Discount): EstimatedDiscountFromPriceChangeWithDiscountId => ({
    discountId,
    value,
    startsAt: new Date(starts_at),
    endsAt: ends_at ? new Date(ends_at) : undefined,
    usageLimit: usage_limit ?? undefined
  }),
  //Front utils
  isAutoDiscount: ({ metadata }: { metadata: Record<string, unknown> }) =>
    metadata?.rule === 'AUTO_DISCOUNT',
  discountIsActive:
    (currentStartsAt: Date | null) =>
    ({ startsAt, endsAt, usageCount, metadata, usageLimit }: PeyceDiscount) =>
      (currentStartsAt ? startsAt <= currentStartsAt : true) &&
      (endsAt ? endsAt >= new Date() : true) &&
      usageCount < (usageLimit ?? Infinity) &&
      metadata?.rule === 'AUTO_DISCOUNT',
  unformattedDiscountIsActive:
    (currentStartsAt: Date | null) =>
    ({ starts_at, ends_at, usage_count, metadata, usage_limit }: Discount) =>
      (currentStartsAt ? new Date(starts_at) <= currentStartsAt : true) &&
      (ends_at ? new Date(ends_at) >= new Date() : true) &&
      usage_count < (usage_limit ?? Infinity) &&
      metadata?.rule === 'AUTO_DISCOUNT',
  filterByActiveDiscounts: (discounts: PeyceDiscount[]) =>
    Array.filter(discounts, AutoDiscountUtils.discountIsActive(new Date())),
  amountSumTotalReduction:
    (currency: Currency) => (discounts: (PeyceDiscount | Discount)[]) =>
      combineArray(MonoidSum)(
        discounts,
        AutoDiscountUtils.getPriceInCurrency(currency)
      ),
  getPriceInCurrency:
    (currency: Currency) =>
    ({ rule: { value }, metadata }: PeyceDiscount | Discount) =>
      value *
      ((
        metadata as {
          rates?: Record<string, number>
        }
      )?.rates?.[currency.toUpperCase()] ?? 1)
}
